import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlass,
  X,
  ChatCircle,
  User,
  Briefcase,
  ArrowRight,
  CircleNotch,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  searchMessaging,
  MESSAGE_TYPE_COLOURS,
} from '@/lib/messagingApi';
import { useDebounce } from '@/lib/useDebounce';
import { cn } from '@/lib/utils';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  matterId?: string;
  onClose: () => void;
  onSelectThread: (threadId: string) => void;
  onSelectMessage: (threadId: string, messageId: string) => void;
}

export function MessageSearchModal({
  matterId,
  onClose,
  onSelectThread,
  onSelectMessage,
}: Props) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query.trim(), 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['messaging', 'search-modal', debouncedQuery, matterId],
    queryFn: () => searchMessaging(debouncedQuery, { matterId, limit: 30 }),
    enabled: debouncedQuery.length >= 2,
    staleTime: 15_000,
  });

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Calculate total results for keyboard navigation
  const totalResults =
    (data?.threads?.length ?? 0) + (data?.messages?.length ?? 0);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, totalResults - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && data) {
        e.preventDefault();
        const threadCount = data.threads?.length ?? 0;
        if (selectedIndex < threadCount) {
          const thread = data.threads?.[selectedIndex];
          if (thread) onSelectThread(thread.id);
        } else {
          const msgIndex = selectedIndex - threadCount;
          const msg = data.messages[msgIndex];
          if (msg) {
            onSelectMessage(msg.threadId, msg.id);
          }
        }
      }
    },
    [data, selectedIndex, totalResults, onSelectThread, onSelectMessage],
  );

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [data]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 pt-[15vh]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <ScrollLock />
      <Card className="w-full max-w-xl overflow-hidden shadow-2xl">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <MagnifyingGlass size={18} className="shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search messages, threads, people..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {isFetching && (
            <CircleNotch size={16} className="shrink-0 animate-spin text-muted-foreground" />
          )}
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {debouncedQuery.length < 2 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search
            </div>
          )}

          {debouncedQuery.length >= 2 && isLoading && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Searching...
            </div>
          )}

          {debouncedQuery.length >= 2 &&
            !isLoading &&
            data &&
            totalResults === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No results for "{debouncedQuery}"
              </div>
            )}

          {data && data.threads && data.threads.length > 0 && (
            <div>
              <div className="sticky top-0 z-10 bg-card px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Threads ({data.threads.length})
              </div>
              {data.threads.map((thread, i) => (
                <button
                  key={thread.id}
                  type="button"
                  onClick={() => onSelectThread(thread.id)}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-accent/50',
                    selectedIndex === i && 'bg-accent',
                  )}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400">
                    {thread.case ? (
                      <Briefcase size={14} weight="duotone" />
                    ) : (
                      <ChatCircle size={14} weight="duotone" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {thread.subject || thread.case?.caseName || 'Conversation'}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {thread.case && (
                        <span className="truncate">{thread.case.caseNumber}</span>
                      )}
                      <span>
                        {thread.participants.length} participant
                        {thread.participants.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <ArrowRight size={14} className="shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}

          {data && data.messages && data.messages.length > 0 && (
            <div>
              <div className="sticky top-0 z-10 bg-card px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Messages ({data.messages.length})
              </div>
              {data.messages.map((msg, i) => {
                const index = (data.threads?.length ?? 0) + i;
                return (
                  <button
                    key={msg.id}
                    type="button"
                    onClick={() => onSelectMessage(msg.threadId, msg.id)}
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-accent/50',
                      selectedIndex === index && 'bg-accent',
                    )}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <User size={14} weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-foreground">
                          {msg.senderUser
                            ? `${msg.senderUser.firstName} ${msg.senderUser.lastName}`
                            : 'Unknown'}
                        </span>
                        {msg.messageType !== 'general' && (
                          <Badge
                            className={cn(
                              'h-4 px-1 text-[9px]',
                              MESSAGE_TYPE_COLOURS[msg.messageType],
                            )}
                          >
                            {msg.messageType.replace('_', ' ')}
                          </Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(msg.createdAt).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                      <div className="mt-0.5 text-sm text-foreground line-clamp-2">
                        {highlightMatch(msg.body, debouncedQuery)}
                      </div>
                    </div>
                    <ArrowRight size={14} className="mt-2 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="flex items-center justify-between border-t border-border bg-muted/30 px-4 py-2 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="rounded border border-border bg-card px-1 py-0.5 font-mono">
                ↑↓
              </kbd>{' '}
              navigate
            </span>
            <span>
              <kbd className="rounded border border-border bg-card px-1 py-0.5 font-mono">
                Enter
              </kbd>{' '}
              select
            </span>
            <span>
              <kbd className="rounded border border-border bg-card px-1 py-0.5 font-mono">
                Esc
              </kbd>{' '}
              close
            </span>
          </div>
          {totalResults > 0 && (
            <span>
              {totalResults} result{totalResults !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text;

  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-amber-200 dark:bg-amber-700/50 text-foreground">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
